import { useTranslation } from 'react-i18next';
import { Link, useLoaderData, useNavigate } from 'react-router';
import { useTypedSearchParams } from 'react-router-typesafe-routes';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import useApplyFriendsByCode from '@/hooks/contacts/use-apply-friends-by-code';
import useRegistrationDataByCode from '@/hooks/scan-code/use-registration-data-by-code';
import ROUTES, { type PromiseLoaderReturnType } from '@/routes';

export default function ContactRequestInviteRoute() {
  const loaderData = useLoaderData<PromiseLoaderReturnType>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [{ code }] = useTypedSearchParams(ROUTES.CONTACT.REQUEST.INVITE);

  const { data: registrationData } = useRegistrationDataByCode(code);
  const { mutateAsync: applyFriendsByCode, isPending: isApplying } = useApplyFriendsByCode();

  async function handleApply() {
    if (!code) return;

    const result = await applyFriendsByCode(code);

    if (result.Code === 0) {
      navigate(ROUTES.MESSAGES.$path());
    }
  }

  return (
    <PageLayout
      title={t('contact.scan-code.invite.title')}
      constrainHeight
      headerConfig={{
        navigateBack: true,
      }}>
      <LoadingLayout promise={loaderData?.promises}>
        {() =>
          registrationData ? (
            <div className='mx-5 flex flex-1 flex-col items-center'>
              <p className='flex flex-1 flex-col items-center justify-center gap-4 text-sm'>
                <span>{t('contact.scan-code.invite.you-are-using')}</span>

                <span className='font-bold text-primary'>{registrationData}</span>

                <span>{t('contact.scan-code.invite.welcome')}</span>
              </p>

              <button
                type='button'
                disabled={isApplying}
                className='btn btn-primary mt-8 w-full'
                onClick={handleApply}>
                {isApplying && <span className='loading loading-spinner' />}
                {t('contact.scan-code.invite.accept')}
              </button>
            </div>
          ) : (
            <div className='m-4 mt-8 flex flex-col items-center gap-4'>
              <p>{t('contact.scan-code.invite.code-invalid')}</p>

              <Link
                to={ROUTES.CONTACT.REQUEST.SCAN.$path()}
                replace
                className='btn btn-secondary mt-8 w-full'>
                {t('common.confirm')}
              </Link>
            </div>
          )
        }
      </LoadingLayout>
    </PageLayout>
  );
}
